import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth, AuthProvider } from './hooks/useAuth';
import { ToastProvider } from './contexts/ToastContext'; // Импортируем ToastProvider
import { Provider } from 'react-redux';
import store from './store';

// Layout components
import Layout from './components/common/Layout';
import Loading from './components/common/Loading';

// Pages (with lazy loading)
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const StatisticsPage = React.lazy(() => import('./pages/StatisticsPage'));
const AgentsPage = React.lazy(() => import('./pages/AgentsPage'));
const TimeAdjustmentsPage = React.lazy(() => import('./pages/TimeAdjustmentsPage'));
const AgentSchedulePage = React.lazy(() => import('./pages/AgentSchedulePage'));

/**
 * Protected Route Component
 * Ensures routes are only accessible when authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, initialCheckDone } = useAuth();

  // Show loading spinner while checking authentication
  if (loading || !initialCheckDone) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * Public Route Component
 * Routes accessible only when not authenticated
 */
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, initialCheckDone } = useAuth();

  // Show loading spinner while checking authentication
  if (loading || !initialCheckDone) {
    return <Loading />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Main App Component
 */
const AppContent = () => {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <StatisticsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/agents"
          element={
            <ProtectedRoute>
              <Layout>
                <AgentsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* New Route: Agent Schedule */}
        <Route
          path="/agents/:agentId/schedule"
          element={
            <ProtectedRoute>
              <Layout>
                <AgentSchedulePage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/time-adjustments"
          element={
            <ProtectedRoute>
              <Layout>
                <TimeAdjustmentsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Fallback route (404) */}
        <Route
          path="*"
          element={
            <Navigate to="/" replace />
          }
        />
      </Routes>
    </Suspense>
  );
};

/**
 * Root App Component with Providers
 */
const App = () => {
  return (
    <Provider store={store}>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </Provider>
  );
};

export default App;
